import { AlertMessage, AlertType, ButtonType, CustomButton, SpinnerWrapper, TextInput } from "soprasteria-ui-library";
import * as React from "react";
import { PatientApi } from "../../../api/PatientApi";
import { useAuth } from "../../../App";
import { isInRole, UserContext } from "../../../contexts/UserContext";
import { setMessage } from "../../../helpers/MessageHelper";
import { IAddress } from "../../../models/IAddress";
import { IPatient } from "../../../models/IPatient";
import { ALLMÄNTANDVÅRDBAS, SPECIALISTTANDVÅRDBAS, URVALSTANDLÄKAREBAS, TANDREGLERINGBAS, KÄKORTOPEDIBAS, VÅRDANSVARIG, BEDÖMNINGSTANDLÄKARE, LIBRETTOADMIN } from "../../../utils/RoleConstants";
import { DelistPatientModal } from "./Modals/DelistPatientModal";
import { ListPatientForm } from "./ListPatientForm";
import { AdministratePatientButton } from "./PatientButtons/AdministratePatientButton";
import { PatientCurrentListingInformation } from "./PatientCurrentListingInformation";
import { PatientCurrentListingInformationMessage } from "./PatientCurrentListingInformation";
import { PatientTabs } from "./Tabs/PatientTabs";
import { ReactivateDentalCareModal } from "./Modals/ReactivateFreeDentalCareModal";
import { RefrainFreeDentalCareModal } from "./Modals/RefrainFreeDentalCareModal";
import { RegisterOrthodonticLkgCfPatientModal } from "./Modals/RegisterOrthodonticLkgCfPatientModal";
import { UnregisterLkgCfPatientModal } from "./Modals/UnregisterLkgCfPatientModal";
import { formatPersonalSecurityNumberAndOrganisationNumber } from "../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper";
import { useHistory } from "react-router-dom";

interface IPatientStart {
    patientGuidId: string;
}

export const PatientStart: React.FunctionComponent<IPatientStart> = ({ patientGuidId }) => {
    const [successMessage, setSuccessMessage] = React.useState<string>("");
    const abortController = new AbortController();
    const api = new PatientApi(useAuth());
    const [isPatientLoading, setIsPatientLoading] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [patient, setPatient] = React.useState<IPatient>();
    const [renderTabs, setRenderTab] = React.useState<boolean>(true);
    const userInfo = React.useContext(UserContext);
    const hasRoleSystemAdmin = isInRole(LIBRETTOADMIN, userInfo.roles ? userInfo.roles : []);
    const hasRoleSpecialist = isInRole(SPECIALISTTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleAllmantandvard = isInRole(ALLMÄNTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const hasRoleParaply = isInRole(VÅRDANSVARIG, userInfo.roles ? userInfo.roles : []);
    const hasRoleKonsulttandläkare = isInRole(URVALSTANDLÄKAREBAS, userInfo.roles);
    const hasRoleOrtodontist = isInRole(TANDREGLERINGBAS, userInfo.roles);
    const hasRoleLkgCf = isInRole(KÄKORTOPEDIBAS, userInfo.roles ? userInfo.roles : []);
    const passivePatientMessage = "Passiv patient, omfattas inte av avgiftsfri tandvård inom Region Stockholm";
    const hasPatientRefrainedFromFreeDentalCare = "Patienten avstår avgiftsfri tandvård";
    const patientNotListedMessage = "Patienten är inte listad hos någon vårdgivare i Region Stockholm";
    const patientListedAtOtherCareProviderMessage = "Patienten är listad hos en annan vårdgivare i Region Stockholm";
    const history = useHistory();

    React.useEffect(() => {
        loadPatient();

        return () => {
            abortController.abort();
        }
    }, [patientGuidId])

    const loadPatient = (onCallSuccess?: () => any) => {
        setIsPatientLoading(true);
        if (patientGuidId) {
            api.getPatientData(patientGuidId, (data: IPatient) => {
                setPatient(data);

                setErrorMessage("");
                if (onCallSuccess) {
                    onCallSuccess();
                }
            }, (message) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
                setPatient(undefined);
            }, () => {
                setIsPatientLoading(false);
            });
        }
    }

    const onPatientListed = () => {
        setRenderTab(false);
        loadPatient(() => {
            const message = setMessage(patient?.hasActiveOrthodonticReferralOrReferralFromOtherCareProvider ? "Patienten är nu listad på din mottagning. Patienten har aktiva remisser. En begäran om medgivande för att dela dessa med dig har skickats till tidigare vårdgivare." : "Patienten är nu listad på din mottagning", "Patienten är nu listad på din mottagning");
            setSuccessMessage(message);
        });
        setRenderTab(true);
    }

    const renderCurrentListingInformation = () => {
        const currentListing = patient?.currentListing;
        const patientListingCompensation = patient?.patientListingCompensation;
        if (!hasRoleAllmantandvard && !hasRoleSystemAdmin) {
            return null;
        }
        if (currentListing && patientListingCompensation !== undefined && !patient?.isPatientPassive) {
            return <PatientCurrentListingInformation listing={currentListing} patientListingCompensation={patientListingCompensation} revisionEndDate={patient?.revisionEndDate} careNeedAreaNumber={patient?.careNeedAreaNumber} />
        }
    }

    const renderCurrentListingInformationMessage = () => {
        const currentListing = patient?.currentListing;
        const patientListingCompensation = patient?.patientListingCompensation;
        if (!hasRoleAllmantandvard && !hasRoleSystemAdmin) {
            return null;
        }
        if (!patient?.isPatientListed && hasRoleSystemAdmin) {
            return <PatientCurrentListingInformationMessage message={patientNotListedMessage} isSysAdmin={ true } />
        }
        if (!patient?.isPatientListed) {
            return <PatientCurrentListingInformationMessage message={patientNotListedMessage} isSysAdmin={ false } />
        }
        else if (currentListing == undefined && patientListingCompensation == undefined && !patient?.isPatientPassive) {
            return <PatientCurrentListingInformationMessage message={patientListedAtOtherCareProviderMessage} isSysAdmin={ false } />
        }
    }
    const onPatientRegisteredAsLkgCfPatient = () => {
        loadPatient(() => {
            const message = setMessage("Patienten är nu registrerad på din mottagning", "Patienten är nu registrerad på din mottagning");
            setSuccessMessage(message);
        });
    }

    const formatAddress = (address?: IAddress): string => {
        let streetAddress = address?.address ? address.address.trim() + "," : "";
        return `${streetAddress} ${address?.zipCode} ${address?.city}`;
    }

    const renderPatientInformation = () => {
        if (patient) {
            let labelCol = 5;
            let inputCol = 7;

            return (
                <div className="information-wrapper border-right">
                    <h5>Patientuppgifter</h5>
                    <div className=" row">
                        <div className="col-12">
                            <TextInput name="readonlyPnr" label="Personnummer" defaultValue={formatPersonalSecurityNumberAndOrganisationNumber(patient.personalSecurityNumber)} labelCol={labelCol} inputCol={inputCol} inlineLabel readonly />
                            <TextInput name="readonlyName" label="Namn" defaultValue={patient.name} labelCol={labelCol} inputCol={inputCol} inlineLabel readonly />
                            <TextInput name="readonlyAddress" label="Adress" defaultValue={formatAddress(patient?.address)} labelCol={labelCol} inputCol={inputCol} inlineLabel readonly />
                            <TextInput name="readonlyAreaResponsibilityCareProvider" label="Områdesansvarig mottagning" defaultValue={patient.areaResponsibilityCareProvider?.name} labelCol={labelCol} inputCol={inputCol} inlineLabel readonly />
                            <TextInput name="readonlyGuid" label="Patientkod" defaultValue={patient.patientGuidId} labelCol={labelCol} inputCol={inputCol} inlineLabel readonly />
                        </div>
                    </div>
                </div>
            )
        }
    }

    const renderListPatientButton = () => {
        if (!patient || !hasRoleAllmantandvard || hasRoleParaply) {
            return null;
        }
        if (patient.listingStatus.isListable || patient.listingStatus.isPatientListedAtOtherClinicUnderUmbrella) {
            return (
                <>
                    <ListPatientForm patientId={patient ? patient.patientId : 0} onPatientListed={onPatientListed} />
                    <div className="text-right">
                        <CustomButton buttonType={ButtonType.createAlt}
                            data-toggle="modal"
                            data-target="#list-patient-modal"
                            buttonText="Lista patient på min mottagning"
                        />
                    </div>
                </>
            )
        }

        return (
            <div className="text-right">
                <CustomButton buttonType={ButtonType.general}
                    title={patient ? patient.listingStatus.notListableReason : ""}
                    disabled={true}
                    aria-disabled="true"
                    buttonText="Lista patient på min mottagning"
                />
            </div>
        )
    }

    const onPatientDelisted = () => {
        loadPatient(() => {
            const message = setMessage("Patienten är nu avlistad", "Patienten är nu avlistad");
            setSuccessMessage(message);
        });
    }

    const renderDelistPatientModal = () => {
        if (!patient || !hasRoleSystemAdmin) {
            return null;
        }
        if (patient.listingStatus.hasActiveListing) {
            return (
                <>
                    <DelistPatientModal patientId={patient ? patient.patientId : 0} onPatientDelisted={onPatientDelisted} />
                </>
            )
        }
    }

    const onRefrainFreeDentalCareSuccess = () => {
        loadPatient(() => {
            const message = setMessage("Patienten avstår nu avgiftsfri tandvård", "Patienten avstår nu avgiftsfri tandvård");
            setSuccessMessage(message);
        });
    }

    const renderRefrainFreeDentalCareModal = () => {
        if (!patient || !hasRoleSystemAdmin) {
            return null;
        }
        if (patient.patientStatus.canRefrainFreeDentalCare) {
            return (
                <>
                    <RefrainFreeDentalCareModal patientId={patient ? patient.patientId : 0} onRefrainFreeDentalCareSuccess={onRefrainFreeDentalCareSuccess} />
                </>
            )
        }
    }

    const onReactivateFreeDentalCareSuccess = () => {
        loadPatient(() => {
            const message = setMessage("Patienten tar nu åter del av avgiftsfri tandvård", "Patienten tar nu åter del av avgiftsfri tandvård");
            setSuccessMessage(message);
        });
    }

    const onUnregisterLkgCfPatientSuccess = () => {
        loadPatient(() => {
            const message = setMessage("Patienten har avregistrerats från käkortoped", "Patienten har avregistrerats från käkortoped ");
            setSuccessMessage(message);
        });
    }

    const renderReactivateFreeDentalCareModal = () => {
        if (!patient || !hasRoleSystemAdmin) {
            return null;
        }
        if (patient.patientStatus.canReactivateFreeDentalCare) {
            return (
                <>
                    <ReactivateDentalCareModal patientId={patient ? patient.patientId : 0} onReactivateFreeDentalCareSuccess={onReactivateFreeDentalCareSuccess} />
                </>
            )
        }
    }

    const renderUnregisterLkgCfPatientModal = () => {
        if (patient?.orthodonticLkgCfStatus.canUnregisterPatient) {
            return (
                <>
                    <UnregisterLkgCfPatientModal patientId={patient ? patient.patientId : 0} onUnregisterLkgCfPatient={onUnregisterLkgCfPatientSuccess} />
                </>
            )
        }
    }

    const renderRegisterLkgCfPatientButton = () => {
        if (!patient || !hasRoleLkgCf || hasRoleParaply) {
            return null;
        }
        if (patient.orthodonticLkgCfStatus.canRegisterPatient) {
            return (
                <>
                    <RegisterOrthodonticLkgCfPatientModal patientId={patient ? patient.patientId : 0}
                        isPatientRegisteredAtOtherCareProvider={patient ? patient.orthodonticLkgCfStatus.isPatientRegisteredAtOtherCareProvider : false}
                        onPatientRegistered={onPatientRegisteredAsLkgCfPatient} />
                    <div className="text-right">
                        <CustomButton buttonType={ButtonType.createAlt}
                            data-toggle="modal"
                            data-target="#register-lkgcf-patient-modal"
                            buttonText="Registrera patient på min mottagning"
                        />
                    </div>
                </>
            )
        }

        return (
            <div className="text-right">
                <CustomButton buttonType={ButtonType.general}
                    title={patient ? patient.orthodonticLkgCfStatus.notRegistrableReason : ""}
                    disabled={true}
                    aria-disabled="true"
                    buttonText="Registrera patient på min mottagning"
                />
            </div>
        )
    }

    return (
        <SpinnerWrapper isLoading={isPatientLoading}>
            <section>
                <section className="card">
                    <section className="card-header">
                        <div className="row">
                            <div className="col">
                                <h1>Hantera patient</h1>
                            </div>
                            <div className="col text-right">
                                <AdministratePatientButton canRefrainFromFreeDentalCare={hasRoleSystemAdmin && patient?.patientStatus.canRefrainFreeDentalCare} canReactivateFreeDentalCare={hasRoleSystemAdmin && patient?.patientStatus.canReactivateFreeDentalCare} canDelistPatient={hasRoleSystemAdmin && patient?.listingStatus.hasActiveListing} canUnregisterLkgCfPatient={patient?.orthodonticLkgCfStatus.canUnregisterPatient} />
                            </div>
                        </div>
                    </section>
                    {
                        <section className="card-body">
                            {
                                patient
                                    ?
                                    <>
                                        <AlertMessage alertType={AlertType.success} duration={10000}>{successMessage}</AlertMessage>
                                        {
                                            patient.isPatientPassive ?
                                                <AlertMessage alertType={AlertType.danger} alwaysShow renderOnTopOfPage={false}>{passivePatientMessage}</AlertMessage>
                                                : null
                                        }
                                        {
                                            patient.hasPatientRefrainedFromFreeDentalCare && hasRoleSystemAdmin ?
                                                <AlertMessage alertType={AlertType.danger} alwaysShow renderOnTopOfPage={false}>{hasPatientRefrainedFromFreeDentalCare}</AlertMessage>
                                                : null
                                        }
                                        <div className="row no-form-group-margin-bottom">
                                            <div className="col-6">
                                                {
                                                    renderPatientInformation()
                                                }
                                            </div>
                                            <div className="col-6">
                                                {
                                                    renderCurrentListingInformation()
                                                }
                                                <div className="float-right btn-group" role="group">
                                                    {
                                                        renderListPatientButton()
                                                    }
                                                    {
                                                        renderRegisterLkgCfPatientButton()
                                                    }
                                                </div>

                                                <div className="float-left btn-group" role="group">
                                                    {
                                                        renderCurrentListingInformationMessage()
                                                    }
                                                </div>
                                            </div>
                                            
                                        </div>
                                        {renderDelistPatientModal()}
                                        {renderRefrainFreeDentalCareModal()}
                                        {renderReactivateFreeDentalCareModal()}
                                        {renderUnregisterLkgCfPatientModal()}
                                        <PatientTabs patient={patient} isInRoleSpecialist={hasRoleSpecialist} isInRoleSysadmin={hasRoleSystemAdmin} isInRoleAllmantandvard={hasRoleAllmantandvard} isInRoleParaply={hasRoleParaply} hasRoleKonsulttandläkare={hasRoleKonsulttandläkare} hasRoleOrtodontist={hasRoleOrtodontist} hasRoleLkgCf={hasRoleLkgCf} reloadData={() => loadPatient()} isLoading={setIsPatientLoading} renderTabs={renderTabs} />
                                    </>
                                    : null
                            }
                        </section>
                    }
                    <section className="card-footer">
                        <CustomButton buttonType={ButtonType.general} onClick={() => history.goBack()} buttonText="Gå tillbaka" />
                    </section>
                </section>
            </section>
        </SpinnerWrapper>
    )
}